import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { isDemoSession, readStoredSession, resolveSession, wantsFreshLogin } from './session';
import { initCarnival, submitGameScore, linkCarnivalReport } from './api';
import { sendReport, extractReportIdFromResponse } from '../../functions/sendReport';

const errMsg = (e) => e?.response?.data?.message || e?.message || 'Something went wrong';

// Log the player in (otp / demo / saved session) — see session.js.
export const loginCarnival = createAsyncThunk('carnival/login', async (_, { rejectWithValue }) => {
  try {
    return await resolveSession();
  } catch (e) {
    return rejectWithValue(errMsg(e));
  }
});

// Creates the player's `users` row (100 coins) on the first visit and loads their progress (game.php).
export const loadCarnival = createAsyncThunk('carnival/load', async (_, { getState, rejectWithValue }) => {
  try {
    const res = await initCarnival(getState().carnival.session);
    if (!res.success) return rejectWithValue(res.message || 'Could not load the carnival');
    return res.state;
  } catch (e) {
    return rejectWithValue(errMsg(e));
  }
});

// Push points/time to the ExtraMilePlay reports API from the browser (add on the first call, update after)
// and store the returned report id on the player's users row. Calls are queued so two of them can never
// both "add" a report for the same player.
let reportQueue = Promise.resolve();
export const syncReport = createAsyncThunk('carnival/syncReport', (snapshot, { getState, dispatch }) => {
  const run = async () => {
    const { session, reportId: stateReportId } = getState().carnival;
    if (!session || isDemoSession(session)) return null;
    // Each game runs on its own page, so Redux starts empty when the player comes back and the score is
    // submitted before loadCarnival has restored reportId. The server's users.reportid (passed in by
    // submitGame) is the source of truth — without it this would "add" a new report after every game.
    const reportId = snapshot.reportId || stateReportId;
    const response = await sendReport({
      sessionId: session.sessionId,
      organizationId: session.organizationId,
      userId: session.userId,
      role: session.role,
      token: session.token,
      gameId: session.gameId,
      name: session.name || '',
      points: snapshot.points,
      time: snapshot.time,
      reportId: reportId || undefined,
    });
    // An update (reportId already known) must never change the id: its response can echo other id-ish
    // fields, and adopting one would point every later update at the wrong report.
    if (reportId) {
      if (reportId !== stateReportId) dispatch(reportLinked(reportId));
      return reportId;
    }
    const id = extractReportIdFromResponse(response);
    if (id) {
      dispatch(reportLinked(id));
      await linkCarnivalReport(session, id);
    } else {
      console.error('carnival report: add succeeded but no report id found in response', response);
    }
    return id;
  };
  const p = reportQueue.then(run, run);
  reportQueue = p.catch(() => {});
  return p;
});

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// A finished game: savedata.php + checkcoins.php in one call. Network hiccups are retried (the server
// only ever counts a player's first attempt, so a repeat is harmless).
export const submitGame = createAsyncThunk('carnival/submit', async ({ gameKey, score, time, lines }, { getState, dispatch, rejectWithValue }) => {
  const session = getState().carnival.session;
  let lastErr;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await submitGameScore(session, gameKey, score, time, lines);
      if (res.success) {
        dispatch(syncReport({ points: res.state.points, time: res.state.time, reportId: res.state.reportId }));
        return res;
      }
      return rejectWithValue(res.message || 'Score not accepted');
    } catch (e) {
      // 4xx is a real answer from the server (not a dropped connection): don't retry it.
      if (e?.response && e.response.status < 500) return rejectWithValue(errMsg(e));
      lastErr = e;
      await sleep(400 * (attempt + 1));
    }
  }
  return rejectWithValue(errMsg(lastErr));
});

const slice = createSlice({
  name: 'carnival',
  initialState: {
    session: wantsFreshLogin() ? null : readStoredSession(),
    authStatus: 'idle', // idle -> loading -> succeeded | failed
    authError: null,
    loadStatus: 'idle', // idle -> loading -> succeeded | failed
    loadError: null,
    game: null, // { name, points, time, reportId, games: [{ key, played, score, ... }], allPlayed }
    reportId: null, // id of this player's report on the ExtraMilePlay reports API
    reportTried: false, // the first report is attempted once per login from the shell; later games retry
  },
  reducers: {
    reportLinked: (s, a) => {
      s.reportId = a.payload;
    },
  },
  extraReducers: (b) => {
    b.addCase(loginCarnival.pending, (s) => {
      s.authStatus = 'loading';
    })
      .addCase(loginCarnival.fulfilled, (s, a) => {
        s.session = a.payload;
        s.authStatus = 'succeeded';
        s.authError = null;
        s.game = null; // progress belongs to whoever just logged in
        s.loadStatus = 'idle';
        s.reportId = null;
        s.reportTried = false;
      })
      .addCase(loginCarnival.rejected, (s, a) => {
        s.authStatus = 'failed';
        s.authError = a.payload;
      })
      .addCase(loadCarnival.pending, (s) => {
        s.loadStatus = 'loading';
      })
      .addCase(loadCarnival.fulfilled, (s, a) => {
        s.game = a.payload;
        if (a.payload?.reportId) s.reportId = a.payload.reportId;
        s.loadStatus = 'succeeded';
        s.loadError = null;
      })
      .addCase(loadCarnival.rejected, (s, a) => {
        s.loadStatus = 'failed';
        s.loadError = a.payload;
      })
      .addCase(submitGame.fulfilled, (s, a) => {
        s.game = a.payload.state;
        if (a.payload.state?.reportId) s.reportId = a.payload.state.reportId;
      })
      .addCase(syncReport.pending, (s) => {
        s.reportTried = true;
      })
      .addCase(syncReport.rejected, (s, a) => {
        console.error('carnival report failed:', a.error?.message);
      });
  },
});

export const { reportLinked } = slice.actions;
export default slice.reducer;
